import api from 'lib/trpcClient';
import {
  ClassificationFlow,
  LpInvestorClassification,
} from 'further-types/lp/investor-classification';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';
import { z } from 'zod';
import { onboardingAdmin } from 'further-ui/validations';

type UpdateOnboardingSettingsInput = z.infer<
  typeof onboardingAdmin.SaveOnboardingSettingsInput
>;

const investorClassificationsByFlow = {
  [ClassificationFlow.UK]: [
    LpInvestorClassification.UKHighNetWorthInvestor,
    LpInvestorClassification.UKSophisticatedInvestor,
    LpInvestorClassification.UKProfessionalClient,
  ],
  [ClassificationFlow.EU]: [
    LpInvestorClassification.EUElectiveProfessionalClient,
  ],
  [ClassificationFlow.US]: [
    LpInvestorClassification.USAccreditedInvestor,
    LpInvestorClassification.USQualifiedPurchaser,
    LpInvestorClassification.USQualifiedClient,
  ],
};

const investorClassificationNames = {
  [LpInvestorClassification.UKHighNetWorthInvestor]: 'High net worth',
  [LpInvestorClassification.UKSophisticatedInvestor]: 'Sophisticated',
  [LpInvestorClassification.UKProfessionalClient]: 'Professional',
  [LpInvestorClassification.EUElectiveProfessionalClient]: 'Professional',
  [LpInvestorClassification.USAccreditedInvestor]: 'Accredited investor',
  [LpInvestorClassification.USQualifiedPurchaser]: 'Qualified purchaser',
  [LpInvestorClassification.USQualifiedClient]: 'Qualified client',
};

const toggleInvestorClassificationType = (
  availableInvestorClassifications: Array<LpInvestorClassification>,
  investorClassification: LpInvestorClassification,
  isEnabled: boolean,
) => [
  ...availableInvestorClassifications.filter(
    (classification) => classification !== investorClassification,
  ),
  ...(isEnabled ? [investorClassification] : []),
];

const getFlowDefaultSettings = (
  classificationFlow: ClassificationFlow,
): {
  classificationFlow: ClassificationFlow;
  availableInvestorClassifications: Array<LpInvestorClassification>;
  requiredTestResult: number | null;
} => {
  return {
    classificationFlow,
    availableInvestorClassifications: [],
    requiredTestResult: null,
  };
};

export default function useInvestorAndReviewSettings(vehicleId: string) {
  const {
    data: investorClassificationTypes,
    isLoading: isLoadingInvestorClassificationTypes,
  } = api.investorClassification.listInvestorClassificationTypes.useQuery({
    vehicleId,
  });

  const { onboardingSettings, updateOnboardingSettings, isUpdating } =
    useOnboardingSettings(vehicleId);

  const getFlowSettings = (classificationFlow: ClassificationFlow) => {
    const flow = onboardingSettings.investorClassificationSettings.find(
      (setting) => setting.classificationFlow === classificationFlow,
    );

    const investorClassifications =
      investorClassificationsByFlow[classificationFlow];

    return {
      enabled: !!flow?.availableInvestorClassifications.length,
      requiredTestResult: flow?.requiredTestResult ?? null,
      investorClassifications: investorClassifications.map(
        (classification) => ({
          id: classification,
          name: investorClassificationNames[classification],
          isEnabled:
            !!flow?.availableInvestorClassifications.includes(classification),
        }),
      ),
    };
  };

  const updateFlowSettings = (
    classificationFlow: ClassificationFlow,
    flowSettings: UpdateOnboardingSettingsInput['investorClassificationSettings'][number],
  ) => {
    updateOnboardingSettings({
      investorClassificationSettings:
        onboardingSettings.investorClassificationSettings
          .filter(
            (setting) => setting.classificationFlow !== classificationFlow,
          )
          .map(({ availableClassificationTypeIds, ...rest }) => rest)
          .concat(flowSettings),
    });
  };

  const handleRequestBankDetailsChange = (requestBankDetails: boolean) => {
    updateOnboardingSettings({
      requestBankDetails,
    });
  };

  const handleMinimumCommitmentSizeChange = (
    minimumCommitmentSize: number | null,
  ) => {
    minimumCommitmentSize = minimumCommitmentSize ?? 0;
    if (onboardingSettings.minimumCommitmentSize !== minimumCommitmentSize) {
      updateOnboardingSettings({
        minimumCommitmentSize,
      });
    }
  };

  const handleGeneralCommitmentInstructionsChange = (
    generalCommitmentInstructions: string,
  ) => {
    updateOnboardingSettings({
      generalCommitmentInstructions,
    });
  };

  const handleInvestorClassificationFlowToggle = (
    classificationFlow: ClassificationFlow,
    isEnabled: boolean,
  ) => {
    const availableInvestorClassifications = Array.from(
      new Set(
        isEnabled
          ? investorClassificationTypes
              ?.filter((type) => type.classificationFlow === classificationFlow)
              .map((type) => type.classification) ?? []
          : [],
      ),
    );

    const flowSettings =
      onboardingSettings.investorClassificationSettings.find(
        (setting) => setting.classificationFlow === classificationFlow,
      ) ?? getFlowDefaultSettings(classificationFlow);

    flowSettings.availableInvestorClassifications =
      availableInvestorClassifications;

    if (classificationFlow === ClassificationFlow.US && !isEnabled) {
      onboardingSettings.requireAllSelectedStatuses = false;
      onboardingSettings.requireInvestorExperienceTesting = false;
      onboardingSettings.requireInvestorKnowledgeTesting = false;
    }

    updateFlowSettings(classificationFlow, flowSettings);
  };

  const handleInvestorClassificationTypeToggle = (
    investorClassification: LpInvestorClassification,
    isEnabled: boolean,
  ) => {
    const investorClassificationType = investorClassificationTypes?.find(
      (type) => type.classification === investorClassification,
    );

    if (!investorClassificationType) {
      throw new Error('Investor classification type not found.');
    }

    const classificationFlow = investorClassificationType.classificationFlow;
    const flowSettings =
      onboardingSettings.investorClassificationSettings.find(
        (setting) => setting.classificationFlow === classificationFlow,
      ) ?? getFlowDefaultSettings(classificationFlow);

    flowSettings.availableInvestorClassifications =
      toggleInvestorClassificationType(
        flowSettings.availableInvestorClassifications,
        investorClassification,
        isEnabled,
      );

    if (
      classificationFlow === ClassificationFlow.US &&
      flowSettings.availableInvestorClassifications.length === 0
    ) {
      onboardingSettings.requireAllSelectedStatuses = false;
      onboardingSettings.requireInvestorExperienceTesting = false;
      onboardingSettings.requireInvestorKnowledgeTesting = false;
    }

    updateFlowSettings(classificationFlow, flowSettings);
  };

  const handleUpdateRequiredTestResult = (
    classificationFlow: ClassificationFlow,
    requiredTestResult: number | null,
  ) => {
    const flowSettings =
      onboardingSettings.investorClassificationSettings.find(
        (setting) => setting.classificationFlow === classificationFlow,
      ) ?? getFlowDefaultSettings(classificationFlow);

    flowSettings.requiredTestResult = requiredTestResult;

    updateFlowSettings(classificationFlow, flowSettings);
  };

  const handleDefaultInternationalInvestorClassificationChange = (
    defaultInternationalClassificationFlow: ClassificationFlow,
  ) => {
    updateOnboardingSettings({
      defaultInternationalClassificationFlow,
    });
  };

  const handleRequireAllSelectedStatusesToggle = (isEnabled: boolean) => {
    updateOnboardingSettings({
      requireAllSelectedStatuses: isEnabled,
    });
  };

  const handleRequireInvestorExperienceTestingToggle = (isEnabled: boolean) => {
    updateOnboardingSettings({
      requireInvestorExperienceTesting: isEnabled,
    });
  };

  const handleRequireInvestorKnowledgeTestingToggle = (isEnabled: boolean) => {
    updateOnboardingSettings({
      requireInvestorKnowledgeTesting: isEnabled,
    });
  };

  const isLoading = isUpdating || isLoadingInvestorClassificationTypes;
  const allowFlowDisable =
    onboardingSettings.investorClassificationSettings.filter(
      (setting) => setting.availableInvestorClassifications.length,
    ).length > 1;

  const availableInvestorClassifications =
    onboardingSettings.investorClassificationSettings.flatMap(
      (setting) => setting.availableInvestorClassifications,
    );
  const allowInvestorClassificationDisable =
    availableInvestorClassifications.length > 1;

  const ukFlowSettings = getFlowSettings(ClassificationFlow.UK);
  const euFlowSettings = getFlowSettings(ClassificationFlow.EU);
  const usFlowSettings = getFlowSettings(ClassificationFlow.US);

  const availableFlows = [
    ...(ukFlowSettings.enabled
      ? [{ value: ClassificationFlow.UK, label: 'UK' }]
      : []),
    ...(euFlowSettings.enabled
      ? [{ value: ClassificationFlow.EU, label: 'MiFID' }]
      : []),
    ...(usFlowSettings.enabled
      ? [{ value: ClassificationFlow.US, label: 'US' }]
      : []),
  ];

  return {
    handleRequestBankDetailsChange,
    handleMinimumCommitmentSizeChange,
    handleGeneralCommitmentInstructionsChange,
    handleInvestorClassificationTypeToggle,
    handleInvestorClassificationFlowToggle,
    handleUpdateRequiredTestResult,
    handleDefaultInternationalInvestorClassificationChange,
    handleRequireAllSelectedStatusesToggle,
    handleRequireInvestorExperienceTestingToggle,
    handleRequireInvestorKnowledgeTestingToggle,
    isLoading,
    allowFlowDisable,
    availableFlows,
    allowInvestorClassificationDisable,
    requireAllSelectedStatuses: onboardingSettings.requireAllSelectedStatuses,
    requireInvestorExperienceTesting:
      onboardingSettings.requireInvestorExperienceTesting,
    requireInvestorKnowledgeTesting:
      onboardingSettings.requireInvestorKnowledgeTesting,
    ukFlowSettings,
    euFlowSettings,
    usFlowSettings,
  };
}
